import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { DialogWrapperComponent } from '../dialog-wrapper/dialog-wrapper.component';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { CommunityLoaderComponent } from '../../community-loader/community-loader.component';
import { SnackbarService } from '../../../services/util/snackbar.service';
import { FileService } from '../../../services/api/file.service';
import { Selection } from '../../../interfaces/selection';
import { MessageDialogData } from '../../../interfaces/dialog';

export interface SelectionDialogData {
  mode: 'add' | 'edit';
  selection?: Selection;
}

export interface SelectionDialogResult {
  name: string;
  coverId?: number | null;
}

function createFileFromBlob(blob: Blob, prefix: string): File {
  const extension = blob.type.split('/')[1] || 'jpg';
  return new File([blob], `${prefix}.${extension}`, { type: blob.type });
}

@Component({
  selector: 'outlaw-selection-dialog',
  templateUrl: './selection-dialog.component.html',
  styleUrls: ['./selection-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, DialogWrapperComponent, CommunityLoaderComponent, ImageCropperComponent],
})
export class SelectionDialogComponent {
  private dialogRef = inject(DialogRef<SelectionDialogData>);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);
  private fileService = inject(FileService);
  private snackbarService = inject(SnackbarService);

  data = this.dialogRef.data as SelectionDialogData;

  name = signal(this.data?.selection?.name ?? '');
  coverPreview = signal<string | null>(this.data?.selection?.cover?.url ?? null);
  coverId = signal<number | null>(this.data?.selection?.cover_id ?? null);
  isUploading = signal(false);
  cropperData = signal<{ imageData: string } | null>(null);
  private croppedBlob: Blob | null = null;

  get isValid(): boolean {
    return this.name().trim().length > 0 && !this.isUploading();
  }

  get title(): string {
    return this.data?.mode === 'add' ? 'Add selection' : 'Edit selection';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.snackbarService.show({ message: 'Please select an image file' });
      return;
    }

    const MAX_COVER_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_COVER_SIZE) {
      this.snackbarService.show({ message: 'Image must be smaller than 10 MB' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => this.cropperData.set({ imageData: reader.result as string });
    reader.onerror = () => this.snackbarService.show({ message: 'Failed to read the selected file' });
    reader.readAsDataURL(file);
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.croppedBlob = event.blob ?? null;
  }

  applyCrop(): void {
    if (!this.croppedBlob) return;
    const blob = this.croppedBlob;
    this.cropperData.set(null);
    this.croppedBlob = null;
    this.isUploading.set(true);
    this.coverPreview.set(URL.createObjectURL(blob));

    const file = createFileFromBlob(blob, 'selection_cover');
    this.fileService
      .upload(file, 'cover')
      .pipe(
        finalize(() => this.isUploading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (uploaded) => {
          const blobUrl = this.coverPreview();
          if (blobUrl?.startsWith('blob:')) URL.revokeObjectURL(blobUrl);
          this.coverId.set(uploaded.id);
          this.coverPreview.set(uploaded.url);
        },
        error: () => {
          this.coverPreview.set(null);
          this.snackbarService.show({ message: 'Failed to upload cover' });
        },
      });
  }

  cancelCropper(): void {
    this.cropperData.set(null);
    this.croppedBlob = null;
  }

  removeCover(event: MouseEvent): void {
    event.stopPropagation();
    const ref = this.dialogService.open(MessageDialogComponent, {
      data: {
        message: 'Are you sure you want to remove the cover image?',
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
      } as MessageDialogData,
    });

    ref.afterClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => {
      if (confirmed) {
        const blobUrl = this.coverPreview();
        if (blobUrl?.startsWith('blob:')) URL.revokeObjectURL(blobUrl);
        this.coverPreview.set(null);
        this.coverId.set(null);
      }
    });
  }

  onSave(): void {
    if (!this.isValid) return;
    const result: SelectionDialogResult = {
      name: this.name().trim(),
      coverId: this.coverId(),
    };
    this.dialogRef.close(result);
  }
}
